import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Store, Building2, Users, DollarSign, Package, Truck,
  ArrowRightLeft, Calculator, Calendar, MapPin, User,
  FileText, Download, Upload, Plus, Search, Filter,
  RefreshCw, Eye, Edit, Trash2, MoreHorizontal, ChevronRight,
  Clock, AlertTriangle, CheckCircle, X, TrendingUp, TrendingDown,
  Camera, QrCode, Barcode, Scan, Printer, Bell, UserCheck,
  Home, Database, PieChart, LineChart, BarChart2, Target,
  Activity, Globe, Shield, Award, Star, Heart, Zap, Wrench
} from 'lucide-react';
import { 
  LineChart as RechartsLineChart, Line, AreaChart, Area, BarChart as RechartsBarChart, Bar, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, PieChart as RechartsPieChart, 
  Pie, Cell, ComposedChart
} from 'recharts';

const StoreInventory = () => {
  const [selectedStore, setSelectedStore] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data for store inventory management
  const storesData = [
    {
      id: 1,
      name: 'Store 1',
      location: 'Riyadh',
      status: 'active',
      manager: 'Ahmed Al-Rashid',
      phone: '+966-50-123-4567',
      email: 'ahmed@store1.com',
      address: 'King Fahd Road, Riyadh',
      openingHours: '8:00 AM - 10:00 PM',
      inventoryValue: 125000,
      totalItems: 1250,
      lowStockItems: 15,
      outOfStockItems: 3,
      lastInventoryCount: '2024-01-15',
      nextScheduledCount: '2024-02-15',
      accuracy: 98.5,
      transfers: {
        received: 45,
        sent: 23,
        pending: 5
      },
      receiving: {
        total: 67,
        pending: 8,
        inTransit: 12,
        received: 47
      },
      counting: {
        total: 24,
        scheduled: 3,
        inProgress: 1,
        completed: 20
      },
      performance: {
        sales: 89000,
        growth: 12.5,
        efficiency: 94.2
      },
      staff: [
        { name: 'Ahmed Al-Rashid', role: 'Manager', status: 'active' },
        { name: 'Fatima Hassan', role: 'Assistant Manager', status: 'active' },
        { name: 'Omar Khalil', role: 'Inventory Specialist', status: 'active' },
        { name: 'Layla Ahmed', role: 'Cashier', status: 'active' }
      ],
      recentActivities: [
        { type: 'transfer', description: 'Received 50kg coffee beans', date: '2024-01-18', status: 'completed' },
        { type: 'count', description: 'Completed monthly inventory count', date: '2024-01-15', status: 'completed' },
        { type: 'receiving', description: 'Processed dairy shipment', date: '2024-01-17', status: 'completed' },
        { type: 'alert', description: 'Low stock alert: Organic Milk', date: '2024-01-16', status: 'pending' }
      ]
    },
    {
      id: 2,
      name: 'Store 2',
      location: 'Jeddah',
      status: 'active',
      manager: 'Sara Al-Zahra',
      phone: '+966-50-234-5678',
      email: 'sara@store2.com',
      address: 'Corniche Road, Jeddah',
      openingHours: '8:00 AM - 10:00 PM',
      inventoryValue: 98000,
      totalItems: 980,
      lowStockItems: 22,
      outOfStockItems: 7,
      lastInventoryCount: '2024-01-12',
      nextScheduledCount: '2024-02-12',
      accuracy: 96.8,
      transfers: {
        received: 38,
        sent: 31,
        pending: 3
      },
      receiving: {
        total: 52,
        pending: 5,
        inTransit: 8,
        received: 39
      },
      counting: {
        total: 18,
        scheduled: 2,
        inProgress: 0,
        completed: 16
      },
      performance: {
        sales: 75000,
        growth: 8.3,
        efficiency: 91.5
      },
      staff: [
        { name: 'Sara Al-Zahra', role: 'Manager', status: 'active' },
        { name: 'Khalid Mansour', role: 'Assistant Manager', status: 'active' },
        { name: 'Nour Ibrahim', role: 'Inventory Specialist', status: 'active' }
      ],
      recentActivities: [
        { type: 'transfer', description: 'Sent 30kg tea selection to Store 3', date: '2024-01-19', status: 'completed' },
        { type: 'count', description: 'Scheduled cycle count', date: '2024-01-20', status: 'scheduled' },
        { type: 'receiving', description: 'Bakery items received', date: '2024-01-18', status: 'completed' }
      ]
    },
    {
      id: 3,
      name: 'Store 3',
      location: 'Dammam',
      status: 'active',
      manager: 'Mohammed Al-Sabah',
      phone: '+966-50-345-6789',
      email: 'mohammed@store3.com',
      address: 'King Khalid Road, Dammam',
      openingHours: '8:00 AM - 10:00 PM',
      inventoryValue: 110000,
      totalItems: 1100,
      lowStockItems: 18,
      outOfStockItems: 4,
      lastInventoryCount: '2024-01-14',
      nextScheduledCount: '2024-02-14',
      accuracy: 97.2,
      transfers: {
        received: 42,
        sent: 28,
        pending: 4
      },
      receiving: {
        total: 58,
        pending: 6,
        inTransit: 10,
        received: 42
      },
      counting: {
        total: 22,
        scheduled: 1,
        inProgress: 1,
        completed: 20
      },
      performance: {
        sales: 82000,
        growth: 15.7,
        efficiency: 93.8
      },
      staff: [
        { name: 'Mohammed Al-Sabah', role: 'Manager', status: 'active' },
        { name: 'Aisha Rahman', role: 'Assistant Manager', status: 'active' },
        { name: 'Yusuf Ali', role: 'Inventory Specialist', status: 'active' },
        { name: 'Mariam Saleh', role: 'Cashier', status: 'active' }
      ],
      recentActivities: [
        { type: 'transfer', description: 'Received tea selection from Store 2', date: '2024-01-19', status: 'completed' },
        { type: 'count', description: 'In-progress spot check', date: '2024-01-20', status: 'in-progress' },
        { type: 'receiving', description: 'Produce shipment processing', date: '2024-01-17', status: 'completed' }
      ]
    },
    {
      id: 4,
      name: 'Store 4',
      location: 'Abha',
      status: 'maintenance',
      manager: 'Layla Al-Asiri',
      phone: '+966-50-456-7890',
      email: 'layla@store4.com',
      address: 'Prince Sultan Road, Abha',
      openingHours: '8:00 AM - 9:00 PM',
      inventoryValue: 75000,
      totalItems: 750,
      lowStockItems: 25,
      outOfStockItems: 12,
      lastInventoryCount: '2024-01-10',
      nextScheduledCount: '2024-02-10',
      accuracy: 95.1,
      transfers: {
        received: 28,
        sent: 35,
        pending: 2
      },
      receiving: {
        total: 41,
        pending: 3,
        inTransit: 6,
        received: 32
      },
      counting: {
        total: 16,
        scheduled: 2,
        inProgress: 0,
        completed: 14
      },
      performance: {
        sales: 55000,
        growth: 5.2,
        efficiency: 88.9
      },
      staff: [
        { name: 'Layla Al-Asiri', role: 'Manager', status: 'active' },
        { name: 'Hassan Al-Qahtani', role: 'Assistant Manager', status: 'active' },
        { name: 'Rania Al-Zahrani', role: 'Inventory Specialist', status: 'active' }
      ],
      recentActivities: [
        { type: 'maintenance', description: 'Store under maintenance', date: '2024-01-20', status: 'ongoing' },
        { type: 'transfer', description: 'Emergency transfer to Store 1', date: '2024-01-19', status: 'completed' },
        { type: 'alert', description: 'Multiple out-of-stock items', date: '2024-01-18', status: 'urgent' }
      ]
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500 text-white';
      case 'maintenance': return 'bg-yellow-500 text-white';
      case 'inactive': return 'bg-red-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'transfer': return <ArrowRightLeft className="w-4 h-4" />;
      case 'count': return <Calculator className="w-4 h-4" />;
      case 'receiving': return <Truck className="w-4 h-4" />;
      case 'alert': return <AlertTriangle className="w-4 h-4" />;
      case 'maintenance': return <Wrench className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'transfer': return 'bg-blue-100 text-blue-800';
      case 'count': return 'bg-purple-100 text-purple-800';
      case 'receiving': return 'bg-green-100 text-green-800';
      case 'alert': return 'bg-red-100 text-red-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredData = storesData.filter(store => {
    const matchesSearch = store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         store.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         store.manager.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStore = selectedStore === 'all' || store.id.toString() === selectedStore;
    return matchesSearch && matchesStore;
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart2 },
    { id: 'inventory', label: 'Inventory', icon: Package },
    { id: 'transfers', label: 'Transfers', icon: ArrowRightLeft },
    { id: 'receiving', label: 'Receiving', icon: Truck },
    { id: 'counting', label: 'Counting', icon: Calculator },
    { id: 'staff', label: 'Staff', icon: Users },
    { id: 'activities', label: 'Activities', icon: Activity }
  ];

  const StoreCard = ({ store }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{store.name}</h3>
            <p className="text-sm text-gray-600">{store.location}</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(store.status)}`}>
              {store.status.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Store Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Manager</p>
            <p className="text-sm font-medium text-gray-900">{store.manager}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Phone</p>
            <p className="text-sm font-medium text-gray-900">{store.phone}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Hours</p>
            <p className="text-sm font-medium text-gray-900">{store.openingHours}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Accuracy</p>
            <p className="text-sm font-medium text-gray-900">{store.accuracy}%</p>
          </div>
        </div>

        {/* Inventory Summary */}
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Inventory Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-blue-600">Total Value</p>
              <p className="text-sm font-medium text-blue-900">${store.inventoryValue.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-blue-600">Total Items</p>
              <p className="text-sm font-medium text-blue-900">{store.totalItems}</p>
            </div>
            <div>
              <p className="text-xs text-blue-600">Low Stock</p>
              <p className="text-sm font-medium text-blue-900">{store.lowStockItems}</p>
            </div>
            <div>
              <p className="text-xs text-blue-600">Out of Stock</p>
              <p className="text-sm font-medium text-blue-900">{store.outOfStockItems}</p>
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="mb-4 p-3 bg-green-50 rounded-lg">
          <h4 className="text-sm font-medium text-green-900 mb-2">Performance</h4>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-green-600">Sales</p>
              <p className="text-sm font-medium text-green-900">${store.performance.sales.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-green-600">Growth</p>
              <p className="text-sm font-medium text-green-900">+{store.performance.growth}%</p>
            </div>
            <div>
              <p className="text-xs text-green-600">Efficiency</p>
              <p className="text-sm font-medium text-green-900">{store.performance.efficiency}%</p>
            </div>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Recent Activities</h4>
          <div className="space-y-2">
            {store.recentActivities.slice(0, 3).map((activity, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className={`p-1 rounded ${getActivityColor(activity.type)}`}>
                    {getActivityIcon(activity.type)}
                  </div>
                  <span className="text-sm text-gray-900">{activity.description}</span>
                </div>
                <span className="text-xs text-gray-500">{activity.date}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <Camera className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <QrCode className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <Calculator className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button className="px-3 py-1 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
              View Details
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const MetricCard = ({ title, value, change, icon: Icon, color }) => (
    <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold mt-1 text-gray-900">{value}</p>
          {change && (
            <div className={`flex items-center mt-2 text-sm ${
              change > 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {change > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
              {Math.abs(change)}%
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const totalStores = storesData.length;
  const totalInventoryValue = storesData.reduce((sum, store) => sum + store.inventoryValue, 0);
  const totalItems = storesData.reduce((sum, store) => sum + store.totalItems, 0);
  const averageAccuracy = storesData.reduce((sum, store) => sum + store.accuracy, 0) / totalStores;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Store Management</h2>
            <p className="text-gray-600">Monitor inventory across all store locations</p>
          </div>
          <div className="flex items-center space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Store
            </motion.button>
          </div>
        </div>

        {/* Apple-like Tabs */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-gray-100 rounded-xl p-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Search and Filter */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search stores..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
            />
          </div>
          <select
            value={selectedStore}
            onChange={(e) => setSelectedStore(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
          >
            <option value="all">All Stores</option>
            {storesData.map(store => (
              <option key={store.id} value={store.id}>{store.name}</option>
            ))}
          </select>
          <button className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2">
            <Filter className="w-4 h-4" />
            Advanced Filters
          </button>
        </div>
      </div>

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Stores"
              value={totalStores}
              icon={Store}
              color="bg-blue-500"
            />
            <MetricCard
              title="Total Inventory Value"
              value={`$${totalInventoryValue.toLocaleString()}`}
              icon={DollarSign}
              color="bg-green-500"
            />
            <MetricCard
              title="Total Items"
              value={totalItems.toLocaleString()}
              icon={Package}
              color="bg-purple-500"
            />
            <MetricCard
              title="Average Accuracy"
              value={`${averageAccuracy.toFixed(1)}%`}
              icon={Target}
              color="bg-orange-500"
            />
          </div>

          {/* Store Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <AnimatePresence mode="wait">
              {filteredData.map((store) => (
                <StoreCard key={store.id} store={store} />
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Other Tab Contents */}
      {activeTab === 'inventory' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Inventory Management</h3>
          <p className="text-gray-600">Detailed inventory management features will be implemented here.</p>
        </div>
      )}

      {activeTab === 'transfers' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Transfer Management</h3>
          <p className="text-gray-600">Transfer management features will be implemented here.</p>
        </div>
      )}

      {activeTab === 'receiving' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Receiving Management</h3>
          <p className="text-gray-600">Receiving management features will be implemented here.</p>
        </div>
      )}

      {activeTab === 'counting' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Counting Management</h3>
          <p className="text-gray-600">Counting management features will be implemented here.</p>
        </div>
      )}

      {activeTab === 'staff' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Staff Management</h3>
          <p className="text-gray-600">Staff management features will be implemented here.</p>
        </div>
      )}

      {activeTab === 'activities' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Activity Log</h3>
          <p className="text-gray-600">Activity log features will be implemented here.</p>
        </div>
      )}

      {/* Empty State */}
      {filteredData.length === 0 && (
        <div className="text-center py-12">
          <Store className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No stores found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your search or filter criteria</p>
          <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
            Add New Store
          </button>
        </div>
      )}
    </div>
  );
};

export default StoreInventory; 